// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Grid from 'presentations/views/components/atoms/Grid';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import Headline from 'presentations/views/components/atoms/Headline';
import { gray05, primary } from 'presentations/views/styles/color';
import GoogleMap from 'presentations/views/components/atoms/GoogleMap';
import {
  fontWeight,
  mediaMax,
  mediaMin,
  spacing,
} from 'presentations/views/styles';
import { LocationType } from 'domains/models/event_type/locationType';
import Link from '../../atoms/Link';
import { ViewerItem } from 'presentations/api/schema/user';
import { SharedEventDetailItem } from 'presentations/api/schema/event';

interface Props {
  viewer: ViewerItem | null;
  event: SharedEventDetailItem;
  mapApiKey: string;
  locationId: LocationType | null;
  teamName?: string | null;
  url: string | null;
  address: string | null;
  access: string | null;
  phoneNumber: string | null;
  customLocation: string | null;
}

export default function Component(props: Props) {
  if (!props.locationId) return null;
  const t = translateInComponent(translations);
  const phoneNumber = props.phoneNumber ? `（${props.phoneNumber}）` : '';

  return (
    <Container>
      {props.locationId === LocationType.InPerson && (
        <AccessContainer>
          {props.address && (
            <MapContainer>
              <LocationMap
                address={props.address}
                apiKey={props.mapApiKey}
                width={468}
                height={414}
              />
            </MapContainer>
          )}
          <AccessDescription>
            <BoxSubHeadline variant="h4">{t('Company')}</BoxSubHeadline>
            {props.teamName && <BrandName>{props.teamName}</BrandName>}
            {props.address && <BrandAddress>{props.address}</BrandAddress>}
            {props.access && (
              <>
                <BoxSubHeadline variant="h4">{t('Access')}</BoxSubHeadline>
                <AccessText>{props.access}</AccessText>
              </>
            )}
          </AccessDescription>
        </AccessContainer>
      )}
      {props.locationId === LocationType.Phone && (
        <PhoneContainer>
          <SubHeadline variant="h3">{t('Tool')}</SubHeadline>
          <PhoneContent>
            <PhoneIcon name={'phone'} />
            <PhoneNumber>{`${t('Phone')}${phoneNumber}`}</PhoneNumber>
          </PhoneContent>
        </PhoneContainer>
      )}
      {[
        LocationType.Zoom,
        LocationType.GoogleMeet,
        LocationType.Teams,
      ].includes(props.locationId) && (
        <OnlineContainer>
          <OnlineDescription></OnlineDescription>
          <SubHeadline variant="h3">{t('Tool')}</SubHeadline>
          <Grid container>
            {props.locationId === LocationType.Zoom && (
              <Grid item xs={12} tablet={6}>
                <ToolItem>
                  <Link href="https://zoom.us/" target="_blank">
                    <Image src={'zoom_logo.svg'} alt={t('Zoom')} />
                  </Link>
                  <OnlineTool>
                    {props.url ? (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrlLink href={props.url} target="_blank">
                          {props.url}
                        </OnlineToolUrlLink>
                      </>
                    ) : (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrl>https://zoom.us/</OnlineToolUrl>
                      </>
                    )}
                  </OnlineTool>
                </ToolItem>
              </Grid>
            )}
            {props.locationId === LocationType.GoogleMeet && (
              <Grid item xs={12} tablet={6}>
                <ToolItem>
                  <Link href="https://meet.google.com/" target="_blank">
                    <Image
                      src={'pages/integrations/googlemeet.svg'}
                      alt={t('Google')}
                      height={24}
                    />
                  </Link>
                  <OnlineTool>
                    {props.url ? (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrlLink href={props.url} target="_blank">
                          {props.url}
                        </OnlineToolUrlLink>
                      </>
                    ) : (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrl>https://meet.google.com/</OnlineToolUrl>
                      </>
                    )}
                  </OnlineTool>
                </ToolItem>
              </Grid>
            )}
            {props.locationId === LocationType.Teams && (
              <Grid item xs={12} tablet={6}>
                <ToolItem>
                  <Link
                    href="https://www.microsoft.com/microsoft-teams/group-chat-software"
                    target="_blank"
                  >
                    <Image
                      src={'pages/integrations/teams.svg'}
                      alt={t('Teams')}
                      height={24}
                    />
                  </Link>
                  <OnlineTool>
                    {props.url ? (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrlLink href={props.url} target="_blank">
                          {props.url}
                        </OnlineToolUrlLink>
                      </>
                    ) : (
                      <>
                        <Icon name="link" />
                        <OnlineToolUrl>
                          https://www.microsoft.com/microsoft-teams/group-chat-software
                        </OnlineToolUrl>
                      </>
                    )}
                  </OnlineTool>
                </ToolItem>
              </Grid>
            )}
          </Grid>
        </OnlineContainer>
      )}
    </Container>
  );
}

const Container = styled.div``;
const AccessContainer = styled.div`
  margin-bottom: ${spacing(16)};
  ${mediaMin.tablet} {
    display: flex;
    align-items: top;
    justify-content: space-between;
  }
`;
const BoxSubHeadline = styled(Headline)`
  border-bottom: 1px solid ${gray05};
  padding-bottom: ${spacing(12)};
  margin-bottom: ${spacing(12)};
  width: 100%;
`;
const MapContainer = styled.div`
  width: calc(50% - 20px);
  overflow: hidden;
  ${mediaMax.tablet} {
    margin-bottom: 24px;
  }
  ${mediaMax.tablet} {
    width: 100%;
  }
`;

const LocationMap = styled(GoogleMap)`
  width: 100%;
  height: auto;
  /* margin-left: calc(0px - (512px - 0px) / 2);
  margin-top: calc(0px - (512px - 320px) / 2); */
  ${mediaMin.tablet} {
    /* margin-left: calc(0px - (512px - 446px) / 2);
    margin-top: calc(0px - (512px - 414px) / 2); */
  }
`;

const AccessDescription = styled.div`
  width: calc(50% - 20px);
  ${mediaMax.tablet} {
    padding-left: 0;
    width: 100%;
  }
`;
const BrandName = styled.div`
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;
const BrandAddress = styled.div`
  margin-bottom: ${spacing(20)};
`;
const BrandUrl = styled.div``;
const AccessText = styled.div``;

const OnlineContainer = styled.div``;
const OnlineDescription = styled.div`
  padding-left: ${spacing(40)};
`;
const ToolItem = styled.div`
  margin-bottom: ${spacing(12)};
`;
const OnlineTool = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(12)};
`;
const OnlineToolUrl = styled.span`
  margin-left: ${spacing(8)};
`;
const OnlineToolUrlLink = styled(Link)`
  margin-left: ${spacing(8)};
  word-break: break-all;
  flex: 1;
`;

const PhoneContainer = styled.div``;
const PhoneContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing(8)};
`;
const PhoneIcon = styled(Icon)`
  color: ${primary};
`;
const PhoneNumber = styled.p``;
const SubHeadline = styled(Headline)`
  margin-bottom: ${spacing(16)};
`;
